/**
 * Where things are on the canvas. Flow runs top to bottom: a node's input is
 * the dot on its top edge, its outputs are dots along its bottom edge. The
 * card's CSS and these numbers agree by construction — a port's position is
 * computed here and the card is drawn with the same header height and port
 * row — so wires land on the dots.
 */

import type { NodeSpec, WfNode } from '../types'

export const NODE_W = 240
export const HEAD_H = 56
export const ROW_H = 26
export const PAD_B = 6
/** Vertical gap between a node and the one placed after it. */
export const GAP_Y = 84
/** Horizontal gap between siblings hanging off different ports. */
export const GAP_X = 48

export interface Point {
  x: number
  y: number
}

export function outputsOf(node: WfNode, spec: NodeSpec | undefined): string[] {
  if (!spec) return ['main']
  if (spec.type === 'flow.switch') {
    const cases = (node.params.cases as { name?: string }[] | undefined) ?? []
    return [...cases.map((c) => String(c.name ?? '')).filter(Boolean), 'default']
  }
  return spec.outputs
}

export function isSingleOutput(outputs: string[]): boolean {
  return outputs.length <= 1 && (outputs.length === 0 || outputs[0] === 'main')
}

export function nodeHeight(node: WfNode, spec: NodeSpec | undefined): number {
  const outputs = outputsOf(node, spec)
  return HEAD_H + (isSingleOutput(outputs) ? 0 : ROW_H + PAD_B)
}

export function inputPort(node: WfNode): Point {
  return { x: node.position.x + NODE_W / 2, y: node.position.y }
}

/** The x offset of an output dot inside the card. Ports share the width evenly. */
export function portOffset(outputs: string[], port: string): number {
  if (isSingleOutput(outputs)) return NODE_W / 2
  const index = Math.max(0, outputs.indexOf(port))
  return (NODE_W * (index + 1)) / (outputs.length + 1)
}

export function outputPort(node: WfNode, spec: NodeSpec | undefined, port: string): Point {
  const outputs = outputsOf(node, spec)
  return { x: node.position.x + portOffset(outputs, port), y: node.position.y + nodeHeight(node, spec) }
}

/** A wire from an output dot down to an input dot. */
export function wirePath(a: Point, b: Point): string {
  const dy = Math.max(36, Math.abs(b.y - a.y) * 0.5)
  return `M ${a.x} ${a.y} C ${a.x} ${a.y + dy}, ${b.x} ${b.y - dy}, ${b.x} ${b.y}`
}

let counter = 0
export function newId(prefix: string): string {
  counter += 1
  return `${prefix}_${Date.now().toString(36)}${counter.toString(36)}`
}
